import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  FormKind,
  FormSchema,
  OnboardingFormVersion,
  PreflightKey,
  Station,
} from '@hcc/types';

/** A version as returned in the forms list (created_at/published_at are ISO). */
export interface OnboardingFormVersionSummary {
  id: string;
  form_id: string;
  version: number;
  title: string;
  description: string | null;
  schema: FormSchema;
  published_at: string | null;
  created_at: string;
}

/** Autosaved, unpublished builder state. */
export interface OnboardingFormDraft {
  title: string;
  description: string | null;
  schema: FormSchema;
}

export interface OnboardingFormSummary {
  id: string;
  key: string;
  title: string;
  /** Self-service onboarding task vs desk checklist (kit_handover). */
  kind: FormKind;
  /** For kit_handover forms: the station whose drawer offers it. */
  station: Station | null;
  requires_approval: boolean;
  preflight_key: PreflightKey | null;
  /** Present when there are unpublished changes saved by the builder. */
  draft: OnboardingFormDraft | null;
  archived_at: string | null;
  versions: OnboardingFormVersionSummary[];
}

const KEY = ['onboarding-forms'] as const;

export function useOnboardingForms() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api<{ forms: OnboardingFormSummary[] }>('/api/admin/onboarding-forms')).forms,
  });
}

export function useCreateOnboardingForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; title: string; kind?: FormKind; station?: Station | null }) =>
      api<{ form: OnboardingFormSummary }>('/api/admin/onboarding-forms', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Duplicate an existing form. The copy is created as an unpublished draft and
 * returned so the caller can open it straight in the editor.
 */
export function useCopyOnboardingForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ form: OnboardingFormSummary }>(`/api/admin/onboarding-forms/${id}/copy`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Publish a new immutable version of a form (the builder "Publish" action). */
export function usePublishFormVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      title: string;
      description?: string;
      schema: FormSchema;
    }) =>
      api<{ version: OnboardingFormVersion }>(
        `/api/admin/onboarding-forms/${input.id}/versions`,
        {
          method: 'POST',
          body: JSON.stringify({
            title: input.title,
            description: input.description,
            schema: input.schema,
          }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Autosave the builder's working state. On success we patch the cached forms
 * list in place (rather than invalidate) so that reopening the form restores
 * the draft, without triggering a refetch that would fight live typing. The
 * open editor seeds its state once on mount, so updating the cached form's
 * `draft` here doesn't disrupt the current edit — it only matters next time
 * the editor mounts.
 */
export function useSaveFormDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      title: string;
      description?: string;
      schema: FormSchema;
    }) =>
      api<{ ok: true }>(`/api/admin/onboarding-forms/${input.id}/draft`, {
        method: 'PUT',
        body: JSON.stringify({
          title: input.title,
          description: input.description,
          schema: input.schema,
        }),
      }),
    onSuccess: (_data, input) => {
      qc.setQueryData<OnboardingFormSummary[]>(KEY, (forms) =>
        forms?.map((f) =>
          f.id === input.id
            ? {
                ...f,
                draft: {
                  title: input.title,
                  description: input.description ?? null,
                  schema: input.schema,
                },
              }
            : f,
        ),
      );
    },
  });
}

/** Update form-level settings: HR approval and the pre-employment gate it satisfies. */
export function useUpdateOnboardingForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: {
      id: string;
      title?: string;
      kind?: FormKind;
      station?: Station | null;
      requires_approval?: boolean;
      preflight_key?: PreflightKey | null;
    }) =>
      api<{ form: OnboardingFormSummary }>(`/api/admin/onboarding-forms/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Fetch one published version's schema (HR review of submitted answers). */
export function useFormVersion(versionId: string | undefined) {
  return useQuery({
    queryKey: ['onboarding-form-version', versionId],
    enabled: !!versionId,
    queryFn: async () =>
      (
        await api<{ version: OnboardingFormVersionSummary }>(
          `/api/admin/onboarding-forms/versions/${versionId}`,
        )
      ).version,
  });
}

export function useArchiveOnboardingForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/onboarding-forms/${id}/archive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
